import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { stylingApi } from '../lib/api.js';
import type { FilterPattern } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import { formatNumber } from '../lib/formatters.js';
import { useDateRange } from '../hooks/useDateRange.js';
import { DateRangeSelector } from '../components/DateRangeSelector.js';

/**
 * @graph-node {Page}
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business フィルタパターン分析ページ。よく使われるフィルタ組み合わせと少結果パターンを表示
 * @graph-connects stylingApi [calls] Styling APIクライアント呼び出し
 * @graph-connects queryKeys [calls] クエリキー生成
 * @graph-connects useDateRange [calls] 日付範囲管理フック
 * @graph-connects DateRangeSelector [calls] 日付範囲セレクター描画
 */
export const Route = createFileRoute('/styling/filter-patterns')({
  component: FilterPatternsPage,
});

/** @graph-connects none */
const CATEGORY_LABELS: Record<string, string> = {
  '1': 'トップス（長袖）',
  '2': 'トップス（半袖/ノースリ）',
  '3': 'スカート',
  '4': 'パンツ',
  '5': 'ワンピース',
  '6': 'アウター/羽織り',
};

/** @graph-connects none */
const COLOR_LABELS: Record<string, string> = {
  '1': 'ホワイト',
  '2': 'アイボリー',
  '3': 'ベージュ',
  '4': 'ブラウン',
  '5': 'カーキ',
  '6': 'グリーン',
  '7': 'イエロー',
  '8': 'オレンジ',
  '9': 'ピンク',
  '10': 'レッド',
  '11': 'パープル',
  '12': 'ネイビー',
  '13': 'ブルー',
  '14': 'グレー',
  '15': 'ブラック',
  '16': 'マルチ',
  '17': 'ラベンダー',
  '18': 'サックス',
};

/** @graph-connects none */
function formatFilterIds(ids: string, labels: Record<string, string>): string {
  if (!ids) return '-';
  return ids
    .split(',')
    .map((id) => labels[id] ?? id)
    .join(', ');
}

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business FilterPatternsPage定義
 * @graph-connects none
 */
function FilterPatternsPage() {
  const { t } = useTranslation();
  const { preset, setPreset, startDate, endDate, setCustomDates } = useDateRange('30d');

  const dimensionUsageQuery = useQuery({
    queryKey: queryKeys.styling.filterDimensionUsage(startDate, endDate),
    queryFn: () => stylingApi.getFilterDimensionUsage(startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  const filterQuery = useQuery({
    queryKey: queryKeys.styling.filterPatterns(startDate, endDate),
    queryFn: () => stylingApi.getFilterPatterns(startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  const dimensions = dimensionUsageQuery.data?.dimensions ?? [];
  const dimensionsWithFill = dimensions.map((d) => {
    const rate = d.useRate ?? 0;
    let fill = 'var(--chart-3)';
    if (rate > 0.8) fill = 'var(--chart-1)';
    else if (rate >= 0.2) fill = 'var(--chart-2)';
    return { ...d, fill };
  });
  const patterns = filterQuery.data?.patterns ?? [];
  const topPatterns = patterns.slice(0, 30);
  const lowResultPatterns = [...patterns]
    .filter((p) => p.avgResultCount != null && p.avgResultCount < 10 && p.useCount >= 5)
    .sort((a, b) => (a.avgResultCount ?? 0) - (b.avgResultCount ?? 0))
    .slice(0, 30);

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('styling.filterPatterns.title', 'フィルタパターン分析')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-text-tertiary)]">
            {t(
              'styling.filterPatterns.description',
              'スタイリストが使うフィルタ組み合わせとその結果数',
            )}
          </p>
        </div>
        <DateRangeSelector
          preset={preset}
          onPresetChange={setPreset}
          startDate={startDate}
          endDate={endDate}
          onCustomDatesChange={setCustomDates}
        />
      </div>

      {/* フィルタ条件 利用率 */}
      {dimensions.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-2">
            フィルタ条件 利用率
          </h2>
          <p className="text-xs text-[var(--color-text-muted)] mb-4">
            各フィルタ条件がどの程度使われているか（全
            {formatNumber(dimensionUsageQuery.data?.totalEvents ?? 0)}
            回のフィルタ適用中）
          </p>
          <div style={{ height: Math.max(dimensions.length * 36 + 40, 200) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={dimensionsWithFill}
                layout="vertical"
                margin={{ top: 0, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  type="number"
                  domain={[0, 1]}
                  tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
                  tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                />
                <YAxis
                  type="category"
                  dataKey="dimension"
                  width={130}
                  tick={{ fill: 'var(--color-text-primary)', fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-bg-elevated)',
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-text-primary)',
                  }}
                  formatter={
                    ((value: number) => [`${(value * 100).toFixed(1)}%`, '利用率']) as never
                  }
                />
                <Bar dataKey="useRate" radius={[0, 4, 4, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* 利用時 vs 未使用時の結果件数比較テーブル */}
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="text-left py-2 px-3 text-[var(--color-text-secondary)]">条件</th>
                  <th className="text-right py-2 px-3 text-[var(--color-text-secondary)]">
                    利用回数
                  </th>
                  <th className="text-right py-2 px-3 text-[var(--color-text-secondary)]">
                    利用率
                  </th>
                  <th className="text-right py-2 px-3 text-[var(--color-text-secondary)]">
                    使用時 平均結果数
                  </th>
                  <th className="text-right py-2 px-3 text-[var(--color-text-secondary)]">
                    未使用時 平均結果数
                  </th>
                  <th className="text-right py-2 px-3 text-[var(--color-text-secondary)]">差分</th>
                </tr>
              </thead>
              <tbody>
                {dimensions.map((d, i) => {
                  const diff =
                    d.avgResultWhenUsed != null && d.avgResultWhenNotUsed != null
                      ? d.avgResultWhenUsed - d.avgResultWhenNotUsed
                      : null;
                  return (
                    <tr
                      key={i}
                      className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]"
                    >
                      <td className="py-2 px-3 text-[var(--color-text-primary)]">{d.dimension}</td>
                      <td className="py-2 px-3 text-right text-[var(--color-text-primary)]">
                        {formatNumber(d.useCount)}
                      </td>
                      <td className="py-2 px-3 text-right text-[var(--color-text-primary)]">
                        {d.useRate != null ? `${(d.useRate * 100).toFixed(1)}%` : '-'}
                      </td>
                      <td className="py-2 px-3 text-right text-[var(--color-text-primary)]">
                        {d.avgResultWhenUsed != null ? d.avgResultWhenUsed.toFixed(0) : '-'}
                      </td>
                      <td className="py-2 px-3 text-right text-[var(--color-text-primary)]">
                        {d.avgResultWhenNotUsed != null ? d.avgResultWhenNotUsed.toFixed(0) : '-'}
                      </td>
                      <td className="py-2 px-3 text-right">
                        {diff != null ? (
                          <span
                            className={
                              diff < -20
                                ? 'text-[var(--color-error)] font-medium'
                                : 'text-[var(--color-text-primary)]'
                            }
                          >
                            {diff > 0 ? '+' : ''}
                            {diff.toFixed(0)}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* よく使われるフィルタパターン */}
      {topPatterns.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
            よく使われるフィルタパターン
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="text-left py-2 px-3 text-[var(--color-text-secondary)]">
                    カテゴリ
                  </th>
                  <th className="text-left py-2 px-3 text-[var(--color-text-secondary)]">カラー</th>
                  <th className="text-left py-2 px-3 text-[var(--color-text-secondary)]">サイズ</th>
                  <th className="text-right py-2 px-3 text-[var(--color-text-secondary)]">
                    使用回数
                  </th>
                  <th className="text-right py-2 px-3 text-[var(--color-text-secondary)]">
                    セッション数
                  </th>
                  <th className="text-right py-2 px-3 text-[var(--color-text-secondary)]">
                    平均結果数
                  </th>
                  <th className="text-right py-2 px-3 text-[var(--color-text-secondary)]">
                    少結果回数
                  </th>
                </tr>
              </thead>
              <tbody>
                {topPatterns.map((p, i) => (
                  <FilterRow key={i} pattern={p} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 少結果フィルタパターン */}
      {lowResultPatterns.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
            少結果フィルタパターン（平均10件未満）
          </h2>
          <p className="text-xs text-[var(--color-text-muted)] mb-3">
            結果が少ないフィルタ組み合わせ。在庫不足 or フィルタ条件が厳しすぎる可能性
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="text-left py-2 px-3 text-[var(--color-text-secondary)]">
                    カテゴリ
                  </th>
                  <th className="text-left py-2 px-3 text-[var(--color-text-secondary)]">カラー</th>
                  <th className="text-left py-2 px-3 text-[var(--color-text-secondary)]">サイズ</th>
                  <th className="text-right py-2 px-3 text-[var(--color-text-secondary)]">
                    使用回数
                  </th>
                  <th className="text-right py-2 px-3 text-[var(--color-text-secondary)]">
                    平均結果数
                  </th>
                </tr>
              </thead>
              <tbody>
                {lowResultPatterns.map((p, i) => (
                  <FilterRow key={i} pattern={p} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ローディング・空状態 */}
      {filterQuery.isLoading && (
        <div className="card p-12 text-center text-[var(--color-text-muted)]">
          {t('common.loading', '読み込み中...')}
        </div>
      )}
      {filterQuery.data && patterns.length === 0 && (
        <div className="card p-12 text-center text-[var(--color-text-muted)]">
          対象期間のフィルタデータがありません（result_count
          プロパティが最近追加されたため、直近データのみ対象）
        </div>
      )}
    </div>
  );
}

/** @graph-connects none */
function FilterRow({ pattern: p }: { pattern: FilterPattern }) {
  return (
    <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]">
      <td className="py-2 px-3 text-[var(--color-text-primary)] text-xs">
        {formatFilterIds(p.categories, CATEGORY_LABELS)}
      </td>
      <td className="py-2 px-3 text-[var(--color-text-primary)] text-xs">
        {formatFilterIds(p.colors, COLOR_LABELS)}
      </td>
      <td className="py-2 px-3 text-[var(--color-text-primary)] text-xs font-mono">
        {p.sizes || '-'}
      </td>
      <td className="py-2 px-3 text-right text-[var(--color-text-primary)]">
        {formatNumber(p.useCount)}
      </td>
      <td className="py-2 px-3 text-right text-[var(--color-text-primary)]">
        {formatNumber(p.sessionCount)}
      </td>
      <td className="py-2 px-3 text-right">
        {p.avgResultCount != null ? (
          <span
            className={
              p.avgResultCount < 10
                ? 'text-[var(--color-error)] font-medium'
                : 'text-[var(--color-text-primary)]'
            }
          >
            {p.avgResultCount.toFixed(0)}
          </span>
        ) : (
          '-'
        )}
      </td>
      <td className="py-2 px-3 text-right text-[var(--color-text-primary)]">
        {p.lowResultCount > 0 ? (
          <span className="text-[var(--color-error)]">{p.lowResultCount}</span>
        ) : (
          '0'
        )}
      </td>
    </tr>
  );
}
